/**
 * PDF1 Slide 2 - Cadastro de Médicos
 * Endpoint: /api/v1/visitas/dashboards/medicos-ativos/
 * 
 * Filtros:
 * - Médicos Ativos por Mês: ANO apenas
 * - Opt-in vs Cadastro: ANO + MÊS
 */
import { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Alert, Badge } from "react-bootstrap";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import api from "../../../services/api";
import { traduzirMesesEmDados, MESES_COMPLETOS } from "./utils/mesesUtils";

const Slide2CadastroMedicos = ({ programaId, filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ano = filters?.year || new Date().getFullYear();
  const mes = filters?.month;

  useEffect(() => {
    // Gráfico de barras usa só ano, pizza usa ano+mês
    let url = `/visitas/dashboards/medicos-ativos/?programa_id=${programaId}&ano=${ano}`;
    if (mes !== null && mes !== undefined) {
      url += `&mes=${mes}`;
    }

    api.get(url)
      .then(res => {
        if (res.data && (res.data.evolucao_ativos?.length > 0 || res.data.status_base?.length > 0)) {
          setData(res.data);
        } else {
          setData({ evolucao_ativos: [], status_base: [] });
        }
      })
      .catch((err) => {
        console.error('[Slide2] API error:', err.response?.data || err.message);
        setError(err.response?.data?.error || err.message);
      })
      .finally(() => setLoading(false));
  }, [programaId, ano, mes]);

  const labelAno = `Ano: ${ano}`;
  const labelAnoMes = mes ? `${MESES_COMPLETOS[mes]} ${ano}` : `Ano: ${ano}`;

  const evolucaoTraduzida = traduzirMesesEmDados(Array.isArray(data?.evolucao_ativos) ? data.evolucao_ativos : []);

  if (loading) return <Spinner animation="border" className="d-block mx-auto my-4" />;

  if (error) {
    return (
      <Alert variant="warning" className="m-3">
        <strong>Seção 1 - Cadastro de Médicos:</strong> {error}
      </Alert>
    );
  }

  const evolucaoArr = Array.isArray(data?.evolucao_ativos) ? data.evolucao_ativos : [];
  const statusArr = Array.isArray(data?.status_base) ? data.status_base : [];
  if (data && (data.evolucao_ativos !== undefined && !Array.isArray(data.evolucao_ativos))) {
    console.warn('[Slide2] evolucao_ativos não é array:', data.evolucao_ativos);
  }
  if (data && (data.status_base !== undefined && !Array.isArray(data.status_base))) {
    console.warn('[Slide2] status_base não é array:', data.status_base);
  }
  const hasEvolucao = evolucaoArr.length > 0;
  const hasStatus = statusArr.length > 0;

  const pieData = hasStatus ? [
    {
      id: "Ativo",
      label: "Ativo",
      value: statusArr.find(s => s?.id === "Ativo")?.value || 0,
      color: "#ed7d31"
    },
    {
      id: "Em Prospecção",
      label: "Em Prospecção",
      value: statusArr.find(s => s?.id === "Em Prospecção")?.value || 0,
      color: "#5b9bd5"
    }
  ] : [];

  return (
    <Row>
      {/* Médicos Ativos por Mês - Filtro: ANO apenas */}
      <Col md={8} className="mb-4">
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
            <span>Médicos Ativos por Mês</span>
            <Badge bg="secondary" className="fw-normal">{labelAno}</Badge>
          </Card.Header>
          <Card.Body style={{ height: "300px" }}>
            {hasEvolucao ? (
              <ResponsiveBar
                data={evolucaoTraduzida}
                keys={["ativos"]}
                indexBy="mes"
                margin={{ top: 20, right: 20, bottom: 40, left: 50 }}
                padding={0.4}
                colors={["#ed7d31"]}
                labelTextColor="#fff"
                axisBottom={{ tickSize: 0, tickPadding: 10 }}
                axisLeft={{ tickSize: 0, tickPadding: 10 }}
                enableGridY={true}
                gridYValues={5}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100">
                <span className="text-muted">Sem registros disponíveis</span>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Opt-In vs Cadastro Realizado - Filtro: ANO + MÊS */}
      <Col md={4} className="mb-4">
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
            <span>Opt-In vs Cadastro Realizado</span>
            <Badge bg="primary" className="fw-normal">{labelAnoMes}</Badge>
          </Card.Header>
          <Card.Body style={{ height: "300px" }}>
            {hasStatus ? (
              <ResponsivePie
                data={pieData}
                margin={{ top: 20, right: 20, bottom: 60, left: 20 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                colors={{ datum: "data.color" }}
                enableArcLinkLabels={false}
                arcLabelsTextColor="#fff"
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 40,
                    itemWidth: 90,
                    itemHeight: 18,
                    symbolSize: 12,
                    symbolShape: "circle"
                  }
                ]}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100">
                <span className="text-muted">Sem registros disponíveis</span>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default Slide2CadastroMedicos;
